import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Download, LogOut, RefreshCw, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const STATUSES = ["new", "contacted", "underwriting", "approved", "funded", "declined"];
const COLORS = {
  new: "bg-sky-100 text-sky-800", contacted: "bg-indigo-100 text-indigo-800", underwriting: "bg-amber-100 text-amber-800",
  approved: "bg-lime-100 text-lime-800", funded: "bg-green-600 text-white", declined: "bg-rose-100 text-rose-800",
};
const FIELDS = [
  ["Business", "business_name"], ["Contact", "full_name"], ["Email", "email"], ["Phone", "phone"], ["Product", "product"],
  ["Desired amount", "desired_amount"], ["Monthly revenue", "monthly_revenue"], ["Industry", "industry"], ["Business start", "business_start_date"],
  ["Credit score", "credit_score"], ["Business debt", "business_debt"], ["Bank account", "bank_account_type"],
];

const Badge = ({ s }) => <span className={`rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${COLORS[s] ?? "bg-slate-100"}`}>{s}</span>;
const fmt = (d) => (d ? new Date(d).toLocaleString() : "");

const toCsv = (rows) => {
  const cols = ["created_at", "status", ...FIELDS.map((f) => f[1]), "notes"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
};

const Detail = ({ app, onSaved, onDeleted }) => {
  const [status, setStatus] = useState(app.status);
  const [notes, setNotes] = useState(app.notes ?? "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await http.patch(`/applications/${app.id}`, { status, notes });
      onSaved(data);
      toast.success("Application updated");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm("Delete this application?")) return;
    try { await http.delete(`/applications/${app.id}`); onDeleted(app.id); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  return (
    <div className="mt-6 space-y-6" data-testid="application-detail">
      <dl className="grid grid-cols-2 gap-x-4 gap-y-4 text-sm">
        {FIELDS.map(([l, k]) => (
          <div key={k}><dt className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{l}</dt><dd className="mt-1 break-words font-medium">{app[k]}</dd></div>
        ))}
      </dl>
      <div>
        <label className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Status</label>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="detail-status-select" className="mt-2 w-full rounded-xl border px-3 py-2.5 capitalize">
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div>
        <label className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Notes</label>
        <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="detail-notes-input" className="mt-2 w-full rounded-xl border px-3 py-2.5" />
      </div>
      <div className="flex gap-3">
        <button onClick={save} disabled={busy} data-testid="detail-save-btn" className="flex flex-1 items-center justify-center rounded-full bg-[var(--ink)] py-3 font-semibold text-white hover:bg-[var(--blue)]">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
        </button>
        <button onClick={del} data-testid="detail-delete-btn" className="rounded-full border px-4 text-rose-600 hover:bg-rose-50" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  );
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const [user, setUser] = useState(null);
  const [apps, setApps] = useState([]);
  const [filter, setFilter] = useState("all");
  const [sel, setSel] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/applications");
      setApps(Array.isArray(data) ? data : []);
    } catch (e) {
      if (e?.response?.status === 401) nav("/admin/login");
    } finally { setLoading(false); }
  }, [nav]);

  useEffect(() => {
    http.get("/auth/me").then((r) => { setUser(r.data); load(); }).catch(() => nav("/admin/login"));
  }, [load, nav]);

  const counts = useMemo(() => Object.fromEntries(STATUSES.map((s) => [s, apps.filter((a) => a.status === s).length])), [apps]);
  const rows = filter === "all" ? apps : apps.filter((a) => a.status === filter);

  const logout = async () => {
    await http.post("/auth/logout").catch(() => {});
    localStorage.removeItem("a613_token");
    nav("/admin/login");
  };

  const exportCsv = () => {
    const url = URL.createObjectURL(new Blob([toCsv(rows)], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `613-advance-applications-${filter}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (!user) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[var(--paper)]" data-testid="admin-dashboard">
      <header className="border-b bg-white">
        <div className="mx-auto flex h-20 max-w-[1400px] items-center justify-between px-5 sm:px-8">
          <Link to="/" data-testid="admin-home-link"><img src="/brand/logo.webp" alt="613 Advance" className="h-12 w-auto" /></Link>
          <div className="flex items-center gap-4">
            <span className="hidden text-sm text-slate-600 sm:block">{user.email}</span>
            <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-2 rounded-full border px-4 py-2 text-sm hover:bg-slate-50"><LogOut className="h-4 w-4" /> Sign out</button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-5 py-10 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--green)]">Deal desk</div>
            <h1 className="mt-2 font-display text-3xl font-black italic sm:text-4xl">Applications</h1>
          </div>
          <div className="flex gap-2">
            <button onClick={load} data-testid="admin-refresh-btn" className="flex items-center gap-2 rounded-full border bg-white px-4 py-2 text-sm hover:bg-slate-50"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh</button>
            <button onClick={exportCsv} data-testid="admin-export-btn" className="flex items-center gap-2 rounded-full bg-[var(--ink)] px-4 py-2 text-sm text-white hover:bg-[var(--blue)]"><Download className="h-4 w-4" /> Export CSV</button>
          </div>
        </div>

        <div className="mt-8 flex gap-2 overflow-x-auto pb-2" data-testid="admin-status-filters">
          {["all", ...STATUSES].map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s}`} className={`shrink-0 rounded-full px-4 py-2 text-sm font-medium capitalize transition-colors ${filter === s ? "bg-[var(--blue)] text-white" : "bg-white hover:bg-slate-100"}`}>
              {s} <span className="ml-1 font-mono text-xs opacity-70">{s === "all" ? apps.length : counts[s]}</span>
            </button>
          ))}
        </div>

        <div className="mt-6 overflow-hidden rounded-2xl border bg-white">
          {rows.length === 0 ? (
            <p className="p-10 text-center text-slate-500" data-testid="admin-empty">{loading ? "Loading…" : "No applications yet."}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-left text-sm" data-testid="admin-applications-table">
                <thead className="border-b bg-slate-50 font-mono text-[11px] uppercase tracking-widest text-slate-500">
                  <tr><th className="px-5 py-3">Received</th><th className="px-5 py-3">Business</th><th className="px-5 py-3">Contact</th><th className="px-5 py-3">Product</th><th className="px-5 py-3">Amount</th><th className="px-5 py-3">Status</th></tr>
                </thead>
                <tbody>
                  {rows.map((a) => (
                    <tr key={a.id} onClick={() => setSel(a)} data-testid={`application-row-${a.id}`} className="cursor-pointer border-b last:border-0 hover:bg-sky-50/60">
                      <td className="px-5 py-4 text-slate-500">{fmt(a.created_at)}</td>
                      <td className="px-5 py-4 font-semibold">{a.business_name}</td>
                      <td className="px-5 py-4">{a.full_name}<div className="text-xs text-slate-500">{a.phone}</div></td>
                      <td className="px-5 py-4">{a.product}</td>
                      <td className="px-5 py-4 font-mono text-xs">{a.desired_amount}</td>
                      <td className="px-5 py-4"><Badge s={a.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      <Sheet open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
          {sel && (
            <>
              <SheetHeader>
                <SheetTitle className="font-display text-2xl font-black italic">{sel.business_name}</SheetTitle>
                <p className="text-sm text-slate-500">Received {fmt(sel.created_at)}</p>
              </SheetHeader>
              <Detail
                key={sel.id}
                app={sel}
                onSaved={(u) => { setApps((p) => p.map((a) => (a.id === u.id ? u : a))); setSel(u); }}
                onDeleted={(id) => { setApps((p) => p.filter((a) => a.id !== id)); setSel(null); }}
              />
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
